import React from 'react'
import { Card, Col, Container, Row } from 'react-bootstrap'
import ContentBlock from '../../components/common/component-card/ContentBlock'
import StatisticCard from '../../components/common/widgets/StatisticCard'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Flex from '../../components/common/Flex'
import SubtleBadge from '../../components/common/SubtleBadge'
import { useGetHubInfoByTenantIDQuery } from '../../store/kopiliot-api/tenant'
import {
  getColorForHubInfoStatus,
  HubInfoStatus,
} from '../../shared/models/hub-info.model'
import Spinner from '../../components/spinner/Spinner'
import ProgressBarWithTooltip from '../../components/common/ProgressBarWithTooltip'
import HubMetrics from '../../features/scw-metrics/HubMetrics'
import { useGetSelfQuotaQuery } from '../../store/kopiliot-api/quota'
import TenantId from '../../shared/models/id/tenant-id'
import { CustomerId } from '../../shared/models/id/customer-id'
import AlarmCard from './components/AlarmCard'
import { AlarmSeverity } from '../../shared/models/alarm.model'

interface Props {
  customerID?: string
  tenantID: string
}

const DefaultHomeDashboard = ({ customerID, tenantID }: Props) => {
  const {
    data: hubData,
    isLoading: isLoadingHubInfo,
    isFetching: isFetchingHubInfo,
    error: errorGetHubInfos,
  } = useGetHubInfoByTenantIDQuery(tenantID)

  const {
    data: apiUsageData,
    isLoading: isLoadingApiUsage,
    isFetching: isFetchingApiUsage,
    error: errorGetApiUsage,
  } = useGetSelfQuotaQuery()
  // Check for alarms

  React.useEffect(() => {
    const error = errorGetHubInfos || errorGetApiUsage
    if (error) {
      //toast.error(error)
    }
  }, [errorGetApiUsage, errorGetHubInfos])

  const isLoadingHub = isLoadingHubInfo || isFetchingHubInfo
  const isLoadingApi = isLoadingApiUsage || isFetchingApiUsage
  return (
    <Container fluid className={'h-100'}>
      <Row className={'mt-2'}>
        <Col sm={12} md={6} lg={4} className={'px-1'}>
          <ContentBlock className={'mb-2'}>
            <ContentBlock.Header className={'border-bottom py-1'}>
              <Flex>
                <FontAwesomeIcon
                  icon={'microchip'}
                  className={'me-1 mt-1 text-primary'}
                />
                <h5 className={'text-primary mb-0'}>Devices</h5>
              </Flex>
            </ContentBlock.Header>
            <ContentBlock.Body noLight className={'pt-1 h-100'}>
              {isLoadingHub ? (
                <Spinner />
              ) : (
                <Row>
                  <Col className={'p-1 h-100'}>
                    <StatisticCard
                      title={'Connectés'}
                      value={hubData?.connectedDeviceCount || 0}
                      className={'shadow-none bg-success-subtle'}
                    />
                  </Col>
                  <Col className={'p-1 h-100'}>
                    <StatisticCard
                      value={
                        (hubData?.deviceCount || 0) -
                        (hubData?.connectedDeviceCount || 0)
                      }
                      title={'Déconnectés'}
                      className={'shadow-none bg-danger-subtle'}
                    />
                  </Col>
                  <Col className={'p-1 h-100'}>
                    <StatisticCard
                      title={'Total'}
                      value={hubData?.deviceCount || 0}
                      className={'shadow-none bg-info-subtle'}
                    />
                  </Col>
                </Row>
              )}
            </ContentBlock.Body>
          </ContentBlock>
        </Col>
        <Col sm={12} md={6} lg={4} className={'px-1 h-100'}>
          <ContentBlock>
            <ContentBlock.Header className={'border-bottom py-1'}>
              <Flex>
                <FontAwesomeIcon
                  icon={'triangle-exclamation'}
                  className={'me-1 mt-1 text-primary'}
                />
                <h5 className={'text-primary mb-0'}>Alertes</h5>
              </Flex>
            </ContentBlock.Header>
            <ContentBlock.Body noLight className={'pt-1'}>
              <Row>
                <Col className={'p-1'}>
                  <AlarmCard
                    title={'Assignées'}
                    className={'shadow-none bg-warning-subtle'}
                    tenantID={new TenantId(tenantID)}
                    customerID={
                      customerID ? new CustomerId(customerID) : undefined
                    }
                  />
                </Col>
                <Col className={'p-1'}>
                  <AlarmCard
                    title={'Critiques'}
                    className={'shadow-none bg-danger-subtle'}
                    tenantID={new TenantId(tenantID)}
                    customerID={
                      customerID ? new CustomerId(customerID) : undefined
                    }
                    severity={AlarmSeverity.CRITICAL}
                  />
                </Col>
                <Col className={'p-1'}>
                  <AlarmCard
                    title={'Total'}
                    className={'shadow-none bg-info-subtle'}
                    tenantID={new TenantId(tenantID)}
                    customerID={
                      customerID ? new CustomerId(customerID) : undefined
                    }
                  />
                </Col>
              </Row>
            </ContentBlock.Body>
          </ContentBlock>
        </Col>
        <Col sm={12} md={12} lg={4} className={'px-1 h-100'}>
          <ContentBlock className={'mb-3'}>
            <ContentBlock.Header className={'border-bottom py-1'}>
              <Flex>
                <FontAwesomeIcon
                  icon={['fab', 'hubspot']}
                  className={'me-1 mt-1 text-primary'}
                />
                <h5 className={'text-primary mb-0'}>Hub</h5>
              </Flex>
            </ContentBlock.Header>
            <ContentBlock.Body noLight className={'h-100 '}>
              {isLoadingHub ? (
                <Spinner />
              ) : (
                <>
                  <Flex className={'mb-3'} direction={'column'}>
                    <div>
                      <strong>Nom:</strong> {hubData?.name || 'N/A'}
                    </div>
                    <div>
                      <strong>Status:</strong>
                      <SubtleBadge
                        bg={getColorForHubInfoStatus(
                          hubData?.status || HubInfoStatus.UNKNOWN
                        )}
                        pill
                      >
                        {hubData?.status || HubInfoStatus.UNKNOWN}
                      </SubtleBadge>
                    </div>
                    <div>
                      <strong>Endpoint:</strong> {hubData?.endpoint || 'N/A'}
                    </div>
                  </Flex>
                </>
              )}
            </ContentBlock.Body>
          </ContentBlock>
        </Col>
      </Row>
      <Row>
        <Col sm={12} md={12} lg={8} className={'px-1'}>
          <ContentBlock>
            <ContentBlock.Header>
              <Flex>
                <FontAwesomeIcon
                  icon={['fab', 'hubspot']}
                  className={'me-1 mt-1 text-primary'}
                />
                <h5 className={'text-primary'}>Hub</h5>
              </Flex>
            </ContentBlock.Header>
            <ContentBlock.Body noLight>
              <HubMetrics tenantID={tenantID} />
            </ContentBlock.Body>
          </ContentBlock>
        </Col>
        <Col sm={12} md={12} lg={4} className={'px-1'}>
          <ContentBlock>
            <ContentBlock.Header>
              <h5>Test</h5>
            </ContentBlock.Header>
            <ContentBlock.Body noLight>
              <p>test</p>
            </ContentBlock.Body>
          </ContentBlock>
        </Col>
      </Row>
      <Row>
        <Col className={'px-1'}>
          <ContentBlock className={''}>
            <ContentBlock.Header className={'border-bottom py-1'}>
              <Flex>
                <FontAwesomeIcon
                  icon={['far', 'circle-question']}
                  className={'me-1 mt-1 text-primary'}
                />
                <h5 className={'text-primary mb-0'}>Quotas</h5>
              </Flex>
            </ContentBlock.Header>
            <ContentBlock.Body noLight>
              <Card.Body className="position-relative">
                <Row>
                  <Col md={12} lg={4} className={'border-end'}>
                    <p className={'mb-0'}>Customers</p>
                    <ProgressBarWithTooltip
                      tooltip={`${apiUsageData?.nbCustomers || 0} sur ${
                        apiUsageData?.maxCustomers || 'Unlimited'
                      }`}
                      now={apiUsageData?.nbCustomers || 0}
                      max={apiUsageData?.maxCustomers || 0}
                      className="rounded-pill mb-3"
                    />
                    <p className={'mb-0'}>Assets</p>
                    <ProgressBarWithTooltip
                      tooltip={`${apiUsageData?.nbAssets || 0} sur ${
                        apiUsageData?.maxAssets || 'Unlimited'
                      }`}
                      now={apiUsageData?.nbAssets || 0}
                      max={apiUsageData?.maxAssets || 0}
                      className="rounded-pill mb-3"
                    />
                    <p className={'mb-0'}>Devices</p>
                    <ProgressBarWithTooltip
                      tooltip={`${apiUsageData?.nbDevices || 0} sur ${
                        apiUsageData?.maxDevices || 'Unlimited'
                      }`}
                      now={apiUsageData?.nbDevices || 0}
                      max={apiUsageData?.maxDevices || 0}
                      className="rounded-pill mb-3"
                    />
                  </Col>
                  <Col sm={12} md={12} lg={4} className={'border-end'}>
                    <p className={'mb-0'}>Users</p>
                    <ProgressBarWithTooltip
                      tooltip={`${apiUsageData?.nbUsers || 0} sur ${
                        apiUsageData?.maxUsers || 'Unlimited'
                      }`}
                      now={apiUsageData?.nbUsers || 0}
                      max={apiUsageData?.maxUsers || 0}
                      className="rounded-pill mb-3"
                    />
                    <p className={'mb-0'}>Rule chains</p>
                    <ProgressBarWithTooltip
                      tooltip={`${apiUsageData?.nbRuleChains || 0} sur ${
                        apiUsageData?.maxRuleChains || 'Unlimited'
                      }`}
                      now={apiUsageData?.nbRuleChains || 0}
                      max={apiUsageData?.maxRuleChains || 0}
                      className="rounded-pill mb-3"
                    />
                    <p className={'mb-0'}>Dashboards</p>
                    <ProgressBarWithTooltip
                      tooltip={`${apiUsageData?.nbDashboards || 0} sur ${
                        apiUsageData?.maxDashboards || 'Unlimited'
                      }`}
                      now={apiUsageData?.nbDashboards || 0}
                      max={apiUsageData?.maxDashboards || 0}
                      className="rounded-pill mb-3"
                    />
                  </Col>
                  <Col sm={12} md={12} lg={4}></Col>
                </Row>
              </Card.Body>
            </ContentBlock.Body>
          </ContentBlock>
        </Col>
      </Row>
    </Container>
  )
}

export default DefaultHomeDashboard
